import React, { Component } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import EmailInput from 'components/elements/common/forms/EmailInput';
import TextInput from 'components/elements/common/forms/TextInput';
import PasswordInput from 'components/elements/common/forms/PasswordInput';
import ErrorView from 'elements/common/ErrorView';
import { validateForm, getAxiosError, validatePasswordMatch } from 'components/utils/helpers';
import {
  PASSWORD_REGEX,
  PASSWORD_RULES_LANGUAGE,
} from '../../../utils/constants';
import Page from '../Page';

import './PasswordResetFlow.scss';

const INVALID_CODE_MESSAGE = 'The code you entered is invalid or has expired.';

class PasswordResetFlow extends Component {
  constructor(props) {
    super(props);

    this.state = {
      step: 'email',
      submitting: false,
      apiError: false,
      form: {
        email: '',
        code: '',
        password: '',
        confirmpassword: '',
      },
      errors: {
        password: false,
        confirmpassword: false,
      },
    };
  }

  onChange(evt) {
    this.setState({ form: { ...this.state.form, [evt.target.name]: evt.target.value.trim() } });
  }

  onPasswordBlur(evt) {
    // set state is async, so we must use form values
    const password = evt.target.value.trim();
    const error = password.length && !PASSWORD_REGEX.test(password) ? PASSWORD_RULES_LANGUAGE : false;

    this.setState({ errors: { ...this.state.errors, password: error } });
  }

  onConfirmPasswordBlur(evt) {
    // set state is async, so we must use form values
    const { form: { password } } = this.state;
    const confirmpassword = evt.target.value.trim();
    const error = validatePasswordMatch(password, confirmpassword);

    this.setState({ errors: { ...this.state.errors, confirmpassword: error } });
  }

  onPasswordFocus(evt) {
    this.setState({ errors: { ...this.state.errors, [evt.target.name]: false } });
  }

  async submitEmail() {
    try {
      this.setState({ submitting: true });
      const { email } = this.state.form;
      await axios.post('/api/forgot-password', { email });
      this.setState({ step: 'code' });
    } catch (err) {
      const { message } = getAxiosError(err);
      this.setState({ apiError: message });
    }
    this.setState({ submitting: false });
  }

  async submitCode() {
    try {
      this.setState({ submitting: true });
      const { email, code } = this.state.form;
      await axios.post('/api/verify-code', { email, code });
      this.setState({ step: 'password' });
    } catch (err) {
      this.setState({ apiError: INVALID_CODE_MESSAGE });
    }
    this.setState({ submitting: false });
  }

  async submitPassword() {
    try {
      this.setState({ submitting: true });
      const { email, code, password } = this.state.form;
      await axios.put('/api/reset-password', { email, code, password });
      this.setState({ step: 'done' });
    } catch (err) {
      const { message } = getAxiosError(err);
      if (message.error === 'New password matches one of last {1} passwords ') {
        message.error = 'You appear to be using a previous password. Please set a new password.';
      }
      this.setState({ apiError: message });
    }
    this.setState({ submitting: false });
  }

  onSubmit(evt) {
    evt.preventDefault();
    this.setState({ apiError: false });

    const { step } = this.state;

    if (step === 'email') {
      if (validateForm(['email'])) {
        this.submitEmail();
      }
      return;
    }

    if (step === 'code') {
      if (validateForm(['code'])) {
        this.submitCode();
      }
      return;
    }

    const { form: { password, confirmpassword } } = this.state;
    const error = validatePasswordMatch(password, confirmpassword);
    if (error) {
      this.setState({ errors: { ...this.state.errors, confirmpassword: error } });
      return;
    }

    if (validateForm(['password', 'confirmpassword'])) {
      this.submitPassword();
    }
  }

  renderEmailStep() {
    const { submitting } = this.state;
    return (
      <form method="post" onSubmit={evt => this.onSubmit(evt)} className="password-reset-form">
        <p>Enter your email and we&apos;ll send you a code to reset your password.</p>
        <EmailInput label="Email" name="email" placeholder="name@example.com" onChange={evt => this.onChange(evt)} required={true} />
        <button className="button-submit" type="submit" onClick={evt => this.onSubmit(evt)} disabled={submitting}>Submit</button>
      </form>
    );
  }

  renderCodeStep() {
    const { submitting, form: { email } } = this.state;
    return (
      <form method="post" onSubmit={evt => this.onSubmit(evt)} className="password-reset-form">
        <p>Enter the code sent to <u>{email}</u>.</p>
        <TextInput label="Code" name="code" onChange={evt => this.onChange(evt)} required={true} />
        <div className="password-reset-resend">
          <button type="button" className="password-reset-resend-link" onClick={() => this.submitEmail()} disabled={submitting}>Resend code</button>
        </div>
        <button className="button-submit" type="submit" onClick={evt => this.onSubmit(evt)} disabled={submitting}>Verify</button>
      </form>
    );
  }

  renderPasswordStep() {
    const { errors, submitting } = this.state;
    return (
      <form method="post" onSubmit={evt => this.onSubmit(evt)} className="password-reset-form">
        <div className="registration-passwords">
          <legend>{PASSWORD_RULES_LANGUAGE}</legend>
          <PasswordInput label="Password" name="password" onChange={evt => this.onChange(evt)} onFocus={evt => this.onPasswordFocus(evt)} onBlur={evt => this.onPasswordBlur(evt)} required={true} errorMsg={errors.password} />
          <PasswordInput label="Confirm Password" name="confirmpassword" onChange={evt => this.onChange(evt)} onFocus={evt => this.onPasswordFocus(evt)} onBlur={evt => this.onConfirmPasswordBlur(evt)} required={true} errorMsg={errors.confirmpassword} />
        </div>
        <button className="button-submit" type="submit" onClick={evt => this.onSubmit(evt)} disabled={submitting || errors.password || errors.confirmpassword}>Reset</button>
      </form>
    );
  }

  renderSuccess() {
    return (
      <div className="password-reset-success">
        <p>Your password has been successfully reset. Please <u><Link to="/login">sign in</Link></u></p>
      </div>
    );
  }

  renderStep() {
    switch (this.state.step) {
      case 'code':
        return this.renderCodeStep();
      case 'password':
        return this.renderPasswordStep();
      case 'done':
        return this.renderSuccess();
      default:
        return this.renderEmailStep();
    }
  }

  render() {
    const { apiError } = this.state;

    return (
      <Page className="password-reset" title="forgotpassword" pageType="forgotpassword">
        <h1>Reset Password</h1>
        {this.renderStep()}
        {apiError && <ErrorView className="password-reset-error" error={apiError} />}
      </Page>
    );
  }
}

export default PasswordResetFlow;
